import React, { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Search, ExternalLink, Trophy, Building2, Sparkles, Database } from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, fmtDateTime, errMsg } from "@/lib/api";

const UFS = ["", "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
const inp = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm";
const EMPTY = { q: "", orgao: "", empresa: "", marca: "", categoria: "", uf: "", de: "", ate: "" };
const MARCA_LBL = { processando: "Buscando…", sem_documento: "Sem documento público", nao_localizado: "Não informada nos documentos", erro: "Erro na leitura" };

function SyncBar({ st, onSync }) {
  return (
    <div data-testid="bench-sync-bar" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <Database className="h-5 w-5 text-sky-600" />
        <div>
          <div data-testid="bench-totals"><b>{st.total_itens ?? 0}</b> itens com resultado oficial · <b>{st.total_compras ?? 0}</b> licitações analisadas</div>
          <div className="text-xs text-slate-400">
            {st.running ? <span className="text-sky-600">{st.fase} · {st.descobertas ?? 0} descobertas · {st.processadas ?? 0} processadas</span>
              : st.finished_at ? `Última coleta: ${fmtDateTime(st.finished_at)}${st.pendentes ? ` · ${st.pendentes} pendentes (continua na próxima coleta diária)` : ""}` : st.started_at ? `Coleta parcial (${st.fase || "interrompida"}) — retomada automaticamente` : "Nenhuma coleta feita ainda"}
          </div>
        </div>
      </div>
      <button data-testid="bench-sync-btn" onClick={onSync} disabled={st.running} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
        {st.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {st.running ? "Coletando…" : "Atualizar histórico (PNCP)"}
      </button>
    </div>
  );
}

function Filtros({ f, setF, cats, onSearch, onClear }) {
  const fld = (k, l, ph, type = "text") => (
    <div><label className="mb-0.5 block text-[11px] font-semibold text-slate-500">{l}</label>
      <input data-testid={`bench-f-${k}`} type={type} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && onSearch()} className={inp} /></div>
  );
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-4 lg:grid-cols-8">
      {fld("q", "Produto", "ex.: papel A4")}{fld("orgao", "Órgão", "nome ou CNPJ")}{fld("empresa", "Empresa vencedora", "nome ou CNPJ")}{fld("marca", "Marca", "ex.: Chamex")}
      <div><label className="mb-0.5 block text-[11px] font-semibold text-slate-500">Categoria</label>
        <select data-testid="bench-f-categoria" value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className={inp}>
          <option value="">Todas</option>{cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select></div>
      <div><label className="mb-0.5 block text-[11px] font-semibold text-slate-500">UF</label>
        <select data-testid="bench-f-uf" value={f.uf} onChange={(e) => setF({ ...f, uf: e.target.value })} className={inp}>{UFS.map((u) => <option key={u} value={u}>{u || "Todas"}</option>)}</select></div>
      {fld("de", "Resultado de", "", "date")}{fld("ate", "até", "", "date")}
      <div className="col-span-2 flex gap-2 md:col-span-4 lg:col-span-8">
        <button data-testid="bench-search-btn" onClick={onSearch} className="flex items-center gap-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"><Search className="h-4 w-4" /> Pesquisar</button>
        <button data-testid="bench-clear-btn" onClick={onClear} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Limpar</button>
      </div>
    </div>
  );
}

function Stats({ d }) {
  const s = d.stats || {};
  const cards = [["Itens encontrados", d.total], ["Preço unit. mínimo", brl(s.min)], ["Preço unit. médio", brl(s.avg)], ["Preço unit. máximo", brl(s.max)],
    ["Desconto médio s/ estimado", s.desconto_medio_pct != null ? `${s.desconto_medio_pct}%` : "—"], ["Valor homologado", brl(s.valor_total)]];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="bench-stats">
      {cards.map(([l, v]) => <div key={l} className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-[11px] font-semibold uppercase text-slate-400">{l}</div><div className="font-display text-lg font-bold text-slate-900">{v ?? "—"}</div></div>)}
    </div>
  );
}

function Rankings({ d }) {
  return (
    <div className="mb-4 grid gap-3 md:grid-cols-2">
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="bench-top-vencedores">
        <div className="mb-2 flex items-center gap-1 text-sm font-bold text-slate-800"><Trophy className="h-4 w-4 text-amber-500" /> Empresas que mais vencem</div>
        {(d.top_vencedores || []).map((v) => <div key={v.cnpj} className="flex justify-between border-b border-slate-50 py-1 text-xs"><span className="line-clamp-1 text-slate-700">{v.nome} <span className="text-slate-400">{v.cnpj}</span></span><span className="shrink-0 font-semibold text-slate-600">{v.vitorias} item(ns) · {brl(v.valor)}</span></div>)}
        {!(d.top_vencedores || []).length && <div className="text-xs text-slate-400">—</div>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="bench-top-orgaos">
        <div className="mb-2 flex items-center gap-1 text-sm font-bold text-slate-800"><Building2 className="h-4 w-4 text-sky-600" /> Órgãos que mais compram</div>
        {(d.top_orgaos || []).map((o) => <div key={o.cnpj} className="flex justify-between border-b border-slate-50 py-1 text-xs"><span className="line-clamp-1 text-slate-700">{o.nome} <span className="text-slate-400">{o.uf}</span></span><span className="shrink-0 font-semibold text-slate-600">{o.itens} item(ns)</span></div>)}
        {!(d.top_orgaos || []).length && <div className="text-xs text-slate-400">—</div>}
      </div>
    </div>
  );
}

function Linha({ r, onMarca }) {
  return (
    <tr className="border-b border-slate-100 align-top text-xs" data-testid="bench-row">
      <td className="px-2 py-2 text-slate-500">{fmtDate(r.data_resultado)}</td>
      <td className="px-2 py-2"><div className="font-semibold text-slate-700">{r.orgao_nome}</div><div className="text-slate-400">{r.municipio}/{r.uf} · {r.modalidade}</div></td>
      <td className="px-2 py-2 text-slate-700"><span className="text-slate-400">#{r.numero_item}</span> {r.descricao}{r.categoria && <div className="mt-0.5 text-[10px] text-sky-600">{r.categoria}</div>}</td>
      <td className="px-2 py-2 text-right">{r.quantidade_homologada ?? r.quantidade} <span className="text-slate-400">{r.unidade}</span></td>
      <td className="px-2 py-2 text-right text-slate-500">{brl(r.valor_unitario_estimado)}</td>
      <td className="px-2 py-2 text-right font-bold text-slate-900">{brl(r.valor_unitario_homologado)}{r.desconto_pct != null && <div className={`text-[10px] font-normal ${r.desconto_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.desconto_pct >= 0 ? (r.desconto_pct === 0 ? "= " : `-${r.desconto_pct}% `) : `+${Math.abs(r.desconto_pct)}% `}vs estimado</div>}</td>
      <td className="px-2 py-2"><div className="text-slate-700">{r.fornecedor_nome}</div><div className="text-slate-400">{r.fornecedor_cnpj} · {r.porte}</div></td>
      <td className="px-2 py-2">
        {r.marca ? <div title={r.marca_fonte?.trecho}><b>{r.marca}</b>{r.modelo && <div className="text-slate-400">{r.modelo}</div>}<div className="text-[10px] text-violet-600">via documento público</div></div>
          : r.marcas_status ? <span className="text-slate-400">{r.marcas_status === "concluido" ? "Não informada nos documentos" : MARCA_LBL[r.marcas_status]}</span>
          : <button data-testid={`bench-marca-btn-${r.id}`} onClick={() => onMarca(r.numero_controle)} className="flex items-center gap-1 rounded border border-violet-200 px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-50"><Sparkles className="h-3 w-3" /> Buscar marca</button>}
      </td>
      <td className="px-2 py-2"><div className="text-[10px] text-slate-400">{r.situacao_resultado}</div><a href={r.link} target="_blank" rel="noreferrer" className="flex items-center gap-0.5 text-sky-600 hover:underline">PNCP <ExternalLink className="h-3 w-3" /></a></td>
    </tr>
  );
}

export default function Benchmark() {
  const [st, setSt] = useState({});
  const [f, setF] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSt = useCallback(() => api.get("/benchmark/sync-status").then((r) => setSt(r.data)).catch(() => {}), []);
  const [applied, setApplied] = useState(EMPTY);
  const search = useCallback(async (pg = 1, filtros = applied) => {
    setLoading(true); setPage(pg); setApplied(filtros);
    try { const { data } = await api.get("/benchmark/items", { params: { ...filtros, page: pg, size: 50 } }); setD(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [applied]);
  useEffect(() => { loadSt(); search(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!st.running) return undefined;
    const t = setInterval(loadSt, 5000);
    return () => clearInterval(t);
  }, [st.running, loadSt]);

  const sync = async () => {
    try { await api.post("/benchmark/sync", {}); toast.success("Coleta iniciada — roda em segundo plano"); loadSt(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const marca = async (nc) => {
    try {
      await api.post("/benchmark/marcas", null, { params: { nc } });
      toast.info("Lendo os documentos públicos da licitação… (IA)");
      setTimeout(() => search(page), 25000);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pages = d ? Math.max(1, Math.ceil(d.total / 50)) : 1;
  return (
    <div className="animate-fade-up" data-testid="benchmark-page">
      <div className="mb-4">
        <h1 className="font-display text-2xl font-bold text-slate-900">PRO INVICTA Benchmark</h1>
        <p className="text-sm text-slate-500">Histórico oficial de licitações homologadas (PNCP): preços, quantidades, empresas vencedoras e resultados — somente dados públicos.</p>
      </div>
      <SyncBar st={st} onSync={sync} />
      <Filtros f={f} setF={setF} cats={st.categorias || []} onSearch={() => search(1, f)} onClear={() => { setF(EMPTY); search(1, EMPTY); }} />
      {!d ? <div className="grid h-40 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
        <>
          <Stats d={d} />
          <Rankings d={d} />
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[1100px]" data-testid="bench-table">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-bold uppercase text-slate-500">
                {["Resultado", "Órgão", "Item", "Qtd", "Unit. estimado", "Unit. homologado", "Vencedor", "Marca", "Fonte"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
              </tr></thead>
              <tbody>{d.items.map((r) => <Linha key={r.id} r={r} onMarca={marca} />)}</tbody>
            </table>
            {d.items.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="bench-empty">{loading ? "Carregando…" : "Nenhum resultado. Ajuste os filtros ou atualize o histórico."}</div>}
          </div>
          <div className="mt-3 flex items-center justify-end gap-2 text-sm">
            <button data-testid="bench-prev" disabled={page <= 1} onClick={() => search(page - 1)} className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Anterior</button>
            <span className="text-slate-500">{page} / {pages}</span>
            <button data-testid="bench-next" disabled={page >= pages} onClick={() => search(page + 1)} className="rounded border border-slate-200 px-3 py-1 disabled:opacity-40">Próxima</button>
          </div>
        </>
      )}
    </div>
  );
}
